import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import {
  applyItem,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Bathroom Estimator (Phase 6). Follows the same pattern kitchen's
 * Phase 5A calculator established: every bathroom-specific answer (see
 * packages/config/src/questions/bathroom.ts) maps onto its own discrete
 * pricing line item(s) rather than a single blanket multiplier, so each
 * of the brief's categories - base, demolition, waterproofing,
 * tub/shower, vanity, toilet, tile, glass, flooring, plumbing,
 * electrical, heated floor, ventilation, cabinetry, design/permits - is
 * independently represented and independently traceable in
 * `explanation.steps`.
 *
 * Tiering pattern: most single-select categories here are always visible
 * (unlike kitchen's electrical/plumbing/structural tiers, which are
 * gated behind a common yes/no/unknown flag) because a bathroom
 * renovation's tub/shower, vanity, toilet, and tile choices are central
 * to the project, not optional side work - see bathroom.ts's own doc
 * comment. When a tiered category is left unanswered, the calculator
 * defaults to a mid-range tier (mirroring kitchen's cabinetry/countertop
 * defaults) rather than silently excluding it, and always records a
 * `..._defaulted` assumption so input-completeness scoring reflects it.
 * A non-empty answer that doesn't match any known option produces an
 * explicit `..._unrecognized_selection` warning plus an exclusion,
 * exactly like kitchen's audit-derived defensive handling - baked in
 * here from the start rather than needing a second audit pass.
 *
 * Waterproofing is not its own question - the brief lists it as scope to
 * "include where relevant," so it's applied automatically (as a
 * percentage of the base renovation, like demolition) whenever there's
 * actual shower/tub or wet-area tile work happening, since waterproofing
 * isn't an optional choice once that work is underway.
 */

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

// --- Existing configuration -> like-for-like replacement item ---------------

const LIKE_FOR_LIKE_ITEM_BY_CONFIG: Record<string, string> = {
  tub: "bathroom_tub_only",
  tub_shower_combo: "bathroom_tub_shower_combo",
  standard_shower: "bathroom_shower_stall",
  walk_in_shower: "bathroom_walk_in_shower_replacement",
  other: "bathroom_shower_stall" // best available fallback - flagged via assumption below
};

const EXISTING_CONFIGURATION_OPTIONS = new Set(["tub", "tub_shower_combo", "standard_shower", "walk_in_shower", "other"]);

// --- Shower/tub scope ---------------------------------------------------

const FIXED_SHOWER_ITEM_BY_SCOPE: Record<string, string> = {
  upgrade: "bathroom_shower_upgrade",
  custom_shower: "bathroom_shower_custom",
  premium_custom: "bathroom_shower_premium"
};

const SHOWER_SCOPE_OPTIONS = new Set(["keep_existing", "replace_like_for_like", "upgrade", "custom_shower", "premium_custom"]);

// --- Vanity ---------------------------------------------------------------

const VANITY_ITEM_BY_TIER: Record<string, string | null> = {
  keep_existing: null,
  standard_replacement: "bathroom_vanity",
  double_vanity: "bathroom_vanity_double",
  semi_custom: "bathroom_vanity_semi_custom",
  custom: "bathroom_vanity_custom"
};

// --- Toilet -----------------------------------------------------------------

const TOILET_ITEM_BY_TIER: Record<string, string | null> = {
  keep_existing: null,
  replace_standard: "bathroom_toilet",
  premium_specialty: "bathroom_toilet_premium"
};

// --- Tile -------------------------------------------------------------------

const TILE_ITEM_BY_SCOPE: Record<string, string | null> = {
  no_tile_changes: null,
  basic_floor_tile: "bathroom_tile",
  floor_and_shower_surround: "bathroom_tile",
  full_height_shower_tile: "bathroom_tile_full_height",
  extensive_custom_tile: "bathroom_tile_extensive_custom"
};

/** How much tile area (relative to floor area) each scope typically covers - walls + floor typically exceed floor area alone. */
const TILE_AREA_FACTOR_BY_SCOPE: Record<string, number> = {
  basic_floor_tile: 1.0,
  floor_and_shower_surround: 1.4,
  full_height_shower_tile: 1.8,
  extensive_custom_tile: 2.2
};

// --- Glass ------------------------------------------------------------------

const GLASS_ITEM_BY_TYPE: Record<string, string | null> = {
  none: null,
  standard: "bathroom_glass",
  frameless: "bathroom_glass_frameless",
  custom: "bathroom_glass_custom"
};

// --- Flooring (non-tile) -----------------------------------------------------

const FLOORING_ITEM_BY_TIER: Record<string, string | null> = {
  no_change: null,
  standard_replacement: "bathroom_flooring_standard",
  mid_range: "bathroom_flooring_mid_range",
  premium: "bathroom_flooring_premium"
};

// --- Plumbing (always visible - "retain existing layout" is itself an option) ---

const PLUMBING_ITEM_BY_TIER: Record<string, string | null> = {
  retain_existing_layout: null,
  minor_changes: "bathroom_plumbing_minor",
  fixture_relocation: "bathroom_plumbing",
  major_relocation: "bathroom_plumbing_major"
};

// --- Electrical (gated behind the common electrical_changes flag, like kitchen) ---

const ELECTRICAL_ITEM_BY_TIER: Record<string, string> = {
  minor_updates: "bathroom_electrical_minor",
  new_lighting: "bathroom_electrical_lighting",
  additional_circuits: "bathroom_electrical",
  significant_changes: "bathroom_electrical_significant"
};

// --- Ventilation --------------------------------------------------------

const VENTILATION_ITEM_BY_TIER: Record<string, string | null> = {
  existing_retained: null,
  upgrade: "bathroom_ventilation",
  new_additional: "bathroom_ventilation_new"
};

export const calculateBathroom: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const roomSqft = input.roomSize?.value;

  // Audit fix (Phase 7 audit): this calculator is shared verbatim by
  // calculateEnsuite (rules/ensuite.ts) - a handful of its warning/
  // exclusion messages below used to hardcode the word "bathroom" even
  // when input.projectType is "ensuite", which directly contradicted the
  // Phase 7 requirement that results accurately identify the project as
  // an ensuite. `projectNoun` is used everywhere one of those messages
  // needs to name the project type in user-facing text.
  const projectNoun = input.projectType === "ensuite" ? "ensuite" : "bathroom";

  // Audit fix (Phase 7 audit): the small-room size-mismatch checks below
  // were calibrated to bathroom's own smallest size bucket (under 35 sq.
  // ft.). Ensuite's own size buckets (see ensuite-size-ranges.ts) start
  // at "under 60", so with the bathroom-calibrated threshold this check
  // could never fire for a bucketed ensuite selection at all - not even
  // a genuinely tight 45 sq. ft. ensuite with a double vanity and a
  // custom shower, the exact scenario it exists to catch. Scaling the
  // threshold to the project type's own smallest realistic size keeps
  // the check meaningful for both.
  const smallRoomThreshold = input.projectType === "ensuite" ? 60 : 35;

  // --- Base renovation (anchor, room-based - not sqft) + scope adjustment ---
  const base = applyItem(catalog, "bathroom_base_renovation", { finishLevel, regionalMultiplier });
  let baseTypicalForPercentage = 0;
  if (base) {
    const scopeMultiplier = getScopeMultiplier(input.scope);
    const scoped = scaleAppliedItem(base, scopeMultiplier);
    applied.push(scoped);
    baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;
    assumptions.push({
      code: "scope_multiplier_applied",
      message: `Base renovation cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
    });
  } else {
    anchorMissing = true;
  }

  // --- Existing configuration (informs "like-for-like" only - no cost of its own) ---
  // Audit fix: an unrecognized value here used to be silently swapped for a
  // "tub_shower_combo" default and still priced as if that were a real
  // answer - inconsistent with every other category in this file (vanity,
  // toilet, tile, glass, flooring, ventilation), which all exclude the
  // cost rather than guess. `resolvedExistingConfig` is still computed for
  // the defaulted (never-answered) case, since a default is a legitimate,
  // disclosed planning assumption - but an unrecognized value no longer
  // feeds pricing at all (see the shower/tub block below, which checks
  // `existingConfigRecognized` directly before using it).
  const existingConfig = rawString(input, "bathroom_existing_configuration");
  const existingConfigRecognized = existingConfig === undefined || EXISTING_CONFIGURATION_OPTIONS.has(existingConfig);
  const resolvedExistingConfig = existingConfig && existingConfigRecognized ? existingConfig : "tub_shower_combo";
  if (!existingConfig) {
    assumptions.push({
      code: "existing_configuration_defaulted",
      message: 'No existing tub/shower configuration was specified - defaulted to "tub/shower combination" for planning purposes.'
    });
  } else if (!existingConfigRecognized) {
    warnings.push({
      code: "bathroom_existing_configuration_unrecognized_selection",
      message: `Existing configuration "${existingConfig}" was not recognized - if a like-for-like tub/shower replacement is selected, that category will be excluded from this estimate rather than guessed at.`,
      severity: "caution"
    });
  } else if (existingConfig === "other") {
    assumptions.push({
      code: "existing_configuration_other_treated_as_shower_stall",
      message: 'Existing configuration was marked "Other" - treated as a standard shower stall for like-for-like pricing purposes.'
    });
  }

  // --- Shower/tub scope --------------------------------------------------
  // Audit fix: an unrecognized scope used to be silently swapped for
  // "replace_like_for_like" and still priced. It's now excluded entirely,
  // consistent with every other category.
  const showerScope = rawString(input, "bathroom_shower_scope");
  const showerScopeRecognized = showerScope === undefined || SHOWER_SCOPE_OPTIONS.has(showerScope);
  const resolvedShowerScope = showerScope && showerScopeRecognized ? showerScope : "replace_like_for_like";
  if (!showerScope) {
    assumptions.push({
      code: "shower_scope_defaulted",
      message: 'No tub/shower scope was specified - defaulted to "replace like-for-like" for planning purposes.'
    });
  } else if (!showerScopeRecognized) {
    warnings.push({
      code: "bathroom_shower_scope_unrecognized_selection",
      message: `Tub/shower scope "${showerScope}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
  }

  let showerApplied = false;
  if (showerScope && !showerScopeRecognized) {
    exclusions.push({
      code: "excludes_shower_tub",
      message: `Tub/shower scope "${showerScope}" could not be matched to a pricing option - no tub/shower cost is included in this estimate.`
    });
  } else if (resolvedShowerScope === "keep_existing") {
    exclusions.push({ code: "excludes_shower_tub", message: "The tub/shower is being kept as-is - no tub/shower cost is included." });
  } else if (resolvedShowerScope === "replace_like_for_like") {
    if (existingConfig && !existingConfigRecognized) {
      warnings.push({
        code: "bathroom_shower_tub_excluded_unrecognized_configuration",
        message: `A like-for-like tub/shower replacement was selected, but the existing configuration "${existingConfig}" was not recognized, so which item to price it as couldn't be determined - no tub/shower cost is included in this estimate.`,
        severity: "caution"
      });
      exclusions.push({
        code: "excludes_shower_tub",
        message: "The existing tub/shower configuration could not be matched to a pricing option, so a like-for-like replacement cost could not be determined."
      });
    } else {
      const itemId = LIKE_FOR_LIKE_ITEM_BY_CONFIG[resolvedExistingConfig] ?? "bathroom_shower_stall";
      const shower = applyItem(catalog, itemId, { finishLevel, regionalMultiplier });
      if (shower) {
        applied.push(shower);
        showerApplied = true;
      }
    }
  } else {
    const itemId = FIXED_SHOWER_ITEM_BY_SCOPE[resolvedShowerScope];
    const shower = itemId ? applyItem(catalog, itemId, { finishLevel, regionalMultiplier }) : null;
    if (shower) {
      applied.push(shower);
      showerApplied = true;
      // A premium/custom shower solution typically needs real floor space
      // to be physically plausible - same pattern as the vanity size-
      // mismatch check below, extended to the category most likely to
      // need it (a $20k+ custom shower in a powder-room-sized bathroom).
      if ((resolvedShowerScope === "custom_shower" || resolvedShowerScope === "premium_custom") && roomSqft && roomSqft < smallRoomThreshold) {
        warnings.push({
          code: "bathroom_shower_size_mismatch",
          message: `A ${resolvedShowerScope.replace(/_/g, " ")} solution was selected for a ${roomSqft} sq. ft. ${projectNoun} - that typically needs more floor space than a small ${projectNoun} provides. This estimate includes the shower cost as selected; please confirm the size is realistic for this space.`,
          severity: "caution"
        });
      }
    }
  }

  // --- Vanity -----------------------------------------------------------
  const vanityTier = rawString(input, "bathroom_vanity");
  const vanityRecognized = vanityTier === undefined || vanityTier in VANITY_ITEM_BY_TIER;
  const vanityItemId = vanityTier ? VANITY_ITEM_BY_TIER[vanityTier] : "bathroom_vanity";
  if (vanityTier === "keep_existing") {
    exclusions.push({ code: "excludes_vanity", message: "The vanity is being kept as-is - no vanity cost is included." });
  } else if (vanityTier && !vanityRecognized) {
    warnings.push({
      code: "bathroom_vanity_unrecognized_selection",
      message: `Vanity selection "${vanityTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({ code: "excludes_vanity", message: `Vanity selection "${vanityTier}" could not be matched to a pricing option - no vanity cost is included in this estimate.` });
  } else if (vanityItemId) {
    const vanity = applyItem(catalog, vanityItemId, { finishLevel, regionalMultiplier });
    if (vanity) {
      applied.push(vanity);
      if (!vanityTier) {
        assumptions.push({ code: "vanity_selection_defaulted", message: 'No vanity selection was provided - defaulted to "standard replacement" for planning purposes.' });
      }
      if ((vanityTier === "double_vanity" || vanityTier === "custom") && roomSqft && roomSqft < smallRoomThreshold) {
        warnings.push({
          code: "bathroom_vanity_size_mismatch",
          message: `A ${vanityTier.replace(/_/g, " ")} was selected for a ${roomSqft} sq. ft. ${projectNoun} - that typically needs more floor space than a small ${projectNoun} provides. This estimate includes the vanity cost as selected; please confirm the size is realistic for this space.`,
          severity: "caution"
        });
      }
    }
  }

  // --- Toilet -----------------------------------------------------------
  const toiletTier = rawString(input, "bathroom_toilet");
  const toiletRecognized = toiletTier === undefined || toiletTier in TOILET_ITEM_BY_TIER;
  const toiletItemId = toiletTier ? TOILET_ITEM_BY_TIER[toiletTier] : "bathroom_toilet";
  if (toiletTier === "keep_existing") {
    exclusions.push({ code: "excludes_toilet", message: "The toilet is being kept as-is - no toilet cost is included." });
  } else if (toiletTier && !toiletRecognized) {
    warnings.push({
      code: "bathroom_toilet_unrecognized_selection",
      message: `Toilet selection "${toiletTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({ code: "excludes_toilet", message: `Toilet selection "${toiletTier}" could not be matched to a pricing option - no toilet cost is included in this estimate.` });
  } else if (toiletItemId) {
    const toilet = applyItem(catalog, toiletItemId, { finishLevel, regionalMultiplier });
    if (toilet) {
      applied.push(toilet);
      if (!toiletTier) {
        assumptions.push({ code: "toilet_selection_defaulted", message: 'No toilet selection was provided - defaulted to "replace with standard" for planning purposes.' });
      }
    }
  }

  // --- Tile (scope drives area; finish level, from the common question, drives quality) ---
  const tileScope = rawString(input, "bathroom_tile_scope");
  const tileRecognized = tileScope === undefined || tileScope in TILE_ITEM_BY_SCOPE;
  const resolvedTileScope = tileScope && tileRecognized ? tileScope : "floor_and_shower_surround";
  let tileApplied = false;
  if (tileScope === "no_tile_changes") {
    exclusions.push({ code: "excludes_tile", message: "No tile changes were selected - no tile cost is included." });
  } else if (tileScope && !tileRecognized) {
    warnings.push({
      code: "bathroom_tile_unrecognized_selection",
      message: `Tile scope "${tileScope}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({ code: "excludes_tile", message: `Tile scope "${tileScope}" could not be matched to a pricing option - no tile cost is included in this estimate.` });
  } else {
    const tileItemId = TILE_ITEM_BY_SCOPE[resolvedTileScope];
    const areaFactor = TILE_AREA_FACTOR_BY_SCOPE[resolvedTileScope] ?? 1.4;
    const tileSqft = roomSqft && roomSqft > 0 ? Math.max(20, Math.round(roomSqft * areaFactor)) : 60;
    const tile = tileItemId ? applyItem(catalog, tileItemId, { quantity: tileSqft, finishLevel, regionalMultiplier }) : null;
    if (tile) {
      applied.push(tile);
      tileApplied = true;
      assumptions.push({
        code: "tile_area_derived",
        message: roomSqft
          ? `Tile area (${tileSqft} sqft) was estimated from room floor area (${roomSqft} sqft) and the selected tile scope.`
          : `No room size was provided; tile area defaulted to ${tileSqft} sqft for a typical bathroom.`
      });
      if (!tileScope) {
        assumptions.push({ code: "tile_scope_defaulted", message: 'No tile scope was provided - defaulted to "floor and shower surround" for planning purposes.' });
      }
    }
  }

  // --- Glass ------------------------------------------------------------
  const glassType = rawString(input, "bathroom_glass_type");
  const glassRecognized = glassType === undefined || glassType in GLASS_ITEM_BY_TYPE;
  const resolvedGlassType = glassType && glassRecognized ? glassType : resolvedExistingConfig === "tub" ? "none" : "standard";
  if (glassType === "none") {
    exclusions.push({ code: "excludes_shower_glass", message: "No shower glass enclosure was selected - excluded from this estimate." });
  } else if (glassType && !glassRecognized) {
    warnings.push({
      code: "bathroom_glass_unrecognized_selection",
      message: `Glass selection "${glassType}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({ code: "excludes_shower_glass", message: `Glass selection "${glassType}" could not be matched to a pricing option - no glass cost is included in this estimate.` });
  } else {
    const glassItemId = GLASS_ITEM_BY_TYPE[resolvedGlassType];
    const glass = glassItemId ? applyItem(catalog, glassItemId, { finishLevel, regionalMultiplier }) : null;
    if (glass) {
      applied.push(glass);
      if (!glassType) {
        assumptions.push({ code: "glass_type_defaulted", message: `No glass selection was provided - defaulted to "${resolvedGlassType}" for planning purposes.` });
      }
    } else if (!glassType && resolvedGlassType === "none") {
      exclusions.push({ code: "excludes_shower_glass", message: 'Existing configuration was "tub only" and no glass selection was provided - assumed no shower glass is needed.' });
    }
    if (resolvedShowerScope === "keep_existing" && resolvedGlassType !== "none") {
      warnings.push({
        code: "bathroom_contradictory_keep_shower_new_glass",
        message:
          "You indicated the tub/shower is being kept as-is, but also selected a new glass enclosure - a new enclosure is usually installed alongside a shower replacement. This estimate includes the glass cost as selected; please confirm which is correct.",
        severity: "caution"
      });
    }
    // Audit fix: this condition was inverted - it fired on a genuinely
    // sensible case (upgrading a tub-only bathroom into one with a real
    // shower, where glass makes sense) and stayed silent on the actually
    // contradictory case (keeping the tub-only configuration as a
    // like-for-like tub, which has no shower to enclose, while still
    // selecting a glass enclosure). "keep_existing" is intentionally not
    // included here - that combination is already covered by the
    // "keep shower + new glass" warning above.
    if (resolvedExistingConfig === "tub" && resolvedGlassType !== "none" && resolvedShowerScope === "replace_like_for_like") {
      warnings.push({
        code: "bathroom_contradictory_tub_only_with_glass",
        message: "A tub-only existing configuration was indicated with a like-for-like replacement (so no shower), but a shower glass enclosure was also selected - please confirm this is intended.",
        severity: "caution"
      });
    }
  }

  // --- Flooring (non-tile) ------------------------------------------------
  const flooringTier = rawString(input, "bathroom_flooring_type");
  const flooringRecognized = flooringTier === undefined || flooringTier in FLOORING_ITEM_BY_TIER;
  if (flooringTier === "no_change" || flooringTier === undefined) {
    exclusions.push({ code: "excludes_flooring", message: "No separate (non-tile) flooring change was selected - no flooring cost is included." });
    if (flooringTier === undefined) {
      assumptions.push({ code: "flooring_defaulted", message: 'No flooring selection was provided - assumed no separate non-tile flooring change for planning purposes.' });
    }
  } else if (!flooringRecognized) {
    warnings.push({
      code: "bathroom_flooring_unrecognized_selection",
      message: `Flooring selection "${flooringTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({ code: "excludes_flooring", message: `Flooring selection "${flooringTier}" could not be matched to a pricing option - no flooring cost is included in this estimate.` });
  } else if (roomSqft && roomSqft > 0) {
    const flooringItemId = FLOORING_ITEM_BY_TIER[flooringTier];
    const flooring = flooringItemId ? applyItem(catalog, flooringItemId, { quantity: roomSqft, finishLevel, regionalMultiplier }) : null;
    if (flooring) {
      applied.push(flooring);
      if (tileApplied && (resolvedTileScope === "basic_floor_tile" || resolvedTileScope === "floor_and_shower_surround" || resolvedTileScope === "extensive_custom_tile")) {
        warnings.push({
          code: "bathroom_contradictory_tile_and_flooring",
          message:
            "Both a floor tile scope and a separate (non-tile) flooring replacement were selected - these usually aren't both needed for the same floor. This estimate includes both costs as selected; please confirm which applies.",
          severity: "caution"
        });
      }
    }
  }

  // --- Waterproofing (automatic - not its own question) -------------------
  const tileNeedsWaterproofing = resolvedTileScope !== "no_tile_changes" && tileApplied && resolvedTileScope !== "basic_floor_tile";
  if ((showerApplied && resolvedShowerScope !== "keep_existing") || tileNeedsWaterproofing) {
    const waterproofing = applyItem(catalog, "bathroom_waterproofing", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (waterproofing) {
      applied.push(waterproofing);
      assumptions.push({
        code: "waterproofing_included_automatically",
        message: "Waterproofing was included automatically because this estimate includes shower/tub or wet-area tile work - it isn't an optional choice once that work is happening."
      });
    }
  }

  // --- Plumbing (always visible; an explicit tier is itself a confirmation) ---
  const plumbingTier = rawString(input, "bathroom_plumbing_scope");
  const plumbingRecognized = plumbingTier === undefined || plumbingTier in PLUMBING_ITEM_BY_TIER;
  if (plumbingTier && !plumbingRecognized) {
    warnings.push({
      code: "bathroom_plumbing_unrecognized_selection",
      message: `Plumbing scope "${plumbingTier}" was not recognized and could not be priced - no plumbing cost is included in this estimate.`,
      severity: "caution"
    });
  } else if (plumbingTier === "retain_existing_layout") {
    // Audit fix: this used to share the "excludes_plumbing" code with the
    // generic engine-level exclusion pushed whenever plumbing_changes ===
    // "no" (see exclusions.ts) - both fire together in the common case,
    // so the results screen (which keys its exclusion list by code) ended
    // up rendering two list items under the same React key. Distinct
    // codes here keep both notes (they say genuinely different things)
    // without the duplicate-key bug.
    exclusions.push({ code: "excludes_bathroom_plumbing_relocation", message: "The plumbing layout is being retained - no plumbing relocation cost is included." });
    if (input.workType.plumbingChanges === "yes") {
      warnings.push({
        code: "bathroom_contradictory_plumbing_changes",
        message: `Plumbing changes were indicated overall, but the ${projectNoun} plumbing scope was set to "retain existing layout" - no plumbing cost is included here; please confirm which is correct.`,
        severity: "caution"
      });
    }
  } else if (plumbingTier) {
    const plumbingItemId = PLUMBING_ITEM_BY_TIER[plumbingTier];
    const plumbing = plumbingItemId ? applyItem(catalog, plumbingItemId, { finishLevel, regionalMultiplier }) : null;
    if (plumbing) applied.push(plumbing);
    if (plumbingTier === "major_relocation" && input.workType.plumbingChanges === "no") {
      warnings.push({
        code: "bathroom_contradictory_plumbing_changes",
        message: `A major plumbing relocation was selected for the ${projectNoun}, but plumbing changes were marked "No" overall - please confirm which is correct.`,
        severity: "caution"
      });
    }
  } else if (input.workType.plumbingChanges === "yes") {
    // No bathroom-specific tier given, but the common flag says there's
    // plumbing work - fall back to the original flat allowance rather than
    // silently excluding it (mirrors kitchen's plumbing/electrical fallback).
    const plumbing = applyItem(catalog, "bathroom_plumbing", { finishLevel, regionalMultiplier });
    if (plumbing) applied.push(plumbing);
  } else if (input.workType.plumbingChanges === "unknown") {
    warnings.push({
      code: "bathroom_plumbing_scope_unknown",
      message: "Plumbing work wasn't confirmed, so no plumbing cost is included in this estimate. If plumbing changes turn out to be needed, this number will need to be revised.",
      severity: "caution"
    });
  } else {
    // Same duplicate-code fix as the "retain_existing_layout" branch above.
    exclusions.push({ code: "excludes_bathroom_plumbing_scope", message: `No ${projectNoun} plumbing work was indicated - no plumbing cost is included.` });
  }

  // --- Electrical (gated behind the common electrical_changes flag) -------
  const electricalTier = rawString(input, "bathroom_electrical_scope");
  if (input.workType.electricalChanges === "yes" || electricalTier) {
    const electricalItemId = electricalTier ? ELECTRICAL_ITEM_BY_TIER[electricalTier] : "bathroom_electrical";
    const electrical = electricalItemId ? applyItem(catalog, electricalItemId, { finishLevel, regionalMultiplier }) : null;
    if (electrical) {
      applied.push(electrical);
    } else if (electricalTier && !(electricalTier in ELECTRICAL_ITEM_BY_TIER)) {
      warnings.push({
        code: "bathroom_electrical_unrecognized_selection",
        message: `Electrical scope "${electricalTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
        severity: "caution"
      });
    }
  } else if (input.workType.electricalChanges === "unknown") {
    warnings.push({
      code: "bathroom_electrical_scope_unknown",
      message: "Electrical work wasn't confirmed, so no electrical cost is included in this estimate. If electrical changes turn out to be needed, this number will need to be revised.",
      severity: "caution"
    });
  }

  // --- Structural changes ---------------------------------------------------
  // Audit fix: bathroom previously had no structural line item at all -
  // "yes" to structural changes (e.g. moving a wall to enlarge the room)
  // produced the exact same estimate as "no", with only the contingency
  // percentage moving (see contingency.ts). The brief's category list
  // didn't call out structural work as its own tiered question the way
  // kitchen's does, so this is a single flat allowance (like kitchen's own
  // flat fallback when no tier is given) rather than a full tier - but a
  // flat, disclosed allowance is materially better than silently
  // including nothing.
  if (input.workType.structuralChanges === "yes") {
    const structural = applyItem(catalog, "bathroom_structural_changes", { finishLevel, regionalMultiplier });
    if (structural) {
      applied.push(structural);
      assumptions.push({
        code: "bathroom_structural_engineering_flag",
        message:
          "Structural or load-bearing wall changes were selected. This estimate includes only a placeholder structural allowance - an engineer's assessment and site evaluation, and likely a structural permit, will be required before final pricing."
      });
    } else {
      warnings.push({
        code: "bathroom_structural_no_applicable_pricing",
        message: "Structural changes were indicated, but no applicable structural pricing could be applied for this configuration - an engineer's assessment is required before this scope can be budgeted.",
        severity: "caution"
      });
    }
  }

  // --- Heated floor (optional add-on) -------------------------------------
  if (input.rawAnswers["bathroom_heated_floor"] === true) {
    const heatedFloorSqft = roomSqft && roomSqft > 0 ? roomSqft : 40;
    const heatedFloor = applyItem(catalog, "bathroom_heated_floor", { quantity: heatedFloorSqft, finishLevel, regionalMultiplier });
    if (heatedFloor) applied.push(heatedFloor);
  } else {
    exclusions.push({ code: "excludes_heated_floor", message: "In-floor heating was not selected - excluded from this estimate." });
  }

  // --- Ventilation --------------------------------------------------------
  const ventilationTier = rawString(input, "bathroom_ventilation_scope");
  const ventilationRecognized = ventilationTier === undefined || ventilationTier in VENTILATION_ITEM_BY_TIER;
  if (ventilationTier === "existing_retained") {
    exclusions.push({ code: "excludes_ventilation", message: "Existing ventilation is being retained - no ventilation cost is included." });
  } else if (ventilationTier && !ventilationRecognized) {
    warnings.push({
      code: "bathroom_ventilation_unrecognized_selection",
      message: `Ventilation scope "${ventilationTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({ code: "excludes_ventilation", message: `Ventilation scope "${ventilationTier}" could not be matched to a pricing option - no ventilation cost is included in this estimate.` });
  } else {
    const ventilationItemId = ventilationTier ? VENTILATION_ITEM_BY_TIER[ventilationTier] : "bathroom_ventilation";
    const ventilation = ventilationItemId ? applyItem(catalog, ventilationItemId, { finishLevel, regionalMultiplier }) : null;
    if (ventilation) {
      applied.push(ventilation);
      if (!ventilationTier) {
        assumptions.push({ code: "ventilation_scope_defaulted", message: 'No ventilation selection was provided - defaulted to "upgrade" for planning purposes.' });
      }
    }
  }

  // --- Custom cabinetry (optional add-on) ---------------------------------
  if (input.rawAnswers["bathroom_custom_cabinetry"] === true) {
    const cabinetry = applyItem(catalog, "bathroom_custom_cabinetry", { finishLevel, regionalMultiplier });
    if (cabinetry) applied.push(cabinetry);
  } else {
    exclusions.push({ code: "excludes_custom_cabinetry", message: "Custom cabinetry was not selected - excluded from this estimate." });
  }

  // --- Demolition -----------------------------------------------------------
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "bathroom_demolition")) {
    const demolition = applyItem(catalog, "bathroom_demolition", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility --------------------------------------------------------
  if (input.accessibility.required === "yes") {
    const accessibility = applyItem(catalog, "bathroom_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Permit / design allowance ---------------------------------------------
  if (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no") {
    const permitDesign = applyItem(catalog, "bathroom_permit_design", { finishLevel, regionalMultiplier });
    if (permitDesign) applied.push(permitDesign);
  }

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
